import { isResponseError, up, type UpFetch } from "up-fetch";
import { type ZodType, z } from "zod";
import { LocalStorageKeys } from "../constants";

export type BaseRequestInput<
  TBodySchema extends ZodType<any> | null,
  TResponseSchema extends ZodType<any> | undefined,
> = {
  url: string;
  method: "GET" | "POST" | "DELETE" | "PUT" | "PATCH";
  bodySchema?: TBodySchema;
  body?: TBodySchema extends ZodType<any> ? z.infer<TBodySchema> : null;
  responseSchema?: TResponseSchema extends ZodType<any>
  ? z.infer<TResponseSchema>
  : undefined;
  headers?: Record<string, string>;
};
type RequestWithBody<
  TBodySchema extends ZodType<any>,
  TResponseSchema extends ZodType<any>,
> = Omit<
  BaseRequestInput<TBodySchema, TResponseSchema>,
  "bodySchema" | "body"
> & {
  bodySchema: TBodySchema;
  body: z.infer<TBodySchema>;
};

export abstract class BaseClient {
  protected client: UpFetch;

  constructor() {
    this.client = up(fetch);
  }

  protected async post<
    TBodySchema extends ZodType<any>,
    TResponseSchema extends ZodType<any>,
  >({
    url,
    bodySchema,
    responseSchema,
    body,
    headers,
  }: Omit<RequestWithBody<TBodySchema, TResponseSchema>, "method">) {
    return await this.request<TBodySchema, TResponseSchema>({
      url,
      method: "POST",
      bodySchema,
      body,
      responseSchema,
      headers,
    });
  }

  protected async put<
    TBodySchema extends ZodType<any>,
    TResponseSchema extends ZodType<any>,
  >({
    url,
    bodySchema,
    responseSchema,
    body,
  }: Omit<RequestWithBody<TBodySchema, TResponseSchema>, "method">) {
    return await this.request<TBodySchema, TResponseSchema>({
      url,
      method: "PUT",
      bodySchema,
      body,
      responseSchema,
    });
  }

  protected async patch<
    TBodySchema extends ZodType<any>,
    TResponseSchema extends ZodType<any>,
  >({
    url,
    bodySchema,
    responseSchema,
    body,
  }: Omit<RequestWithBody<TBodySchema, TResponseSchema>, "method">) {
    return await this.request<TBodySchema, TResponseSchema>({
      url,
      method: "PATCH",
      bodySchema,
      body,
      responseSchema,
    });
  }

  protected async _delete({ url }: { url: string }) {
    return await this.request<null, undefined>({
      url,
      method: "DELETE",
    });
  }

  protected async get<TResponseSchema extends ZodType<any>>({
    url,
    responseSchema,
  }: Omit<BaseRequestInput<null, TResponseSchema>, "method" | "bodySchema">) {
    return await this.request<null, TResponseSchema>({
      url,
      method: "GET",
      responseSchema,
    });
  }

  protected async request<
    TBodySchema extends ZodType<any> | null,
    TResponseSchema extends ZodType<any> | undefined,
  >({
    url,
    method,
    bodySchema,
    body,
    responseSchema,
    headers = { "Content-Type": "application/json" },
  }: BaseRequestInput<TBodySchema, TResponseSchema>) {
    bodySchema?.parse(body);
    const defaultHeaders: Record<string, string> = {
      Authorization: `Bearer ${localStorage.getItem(LocalStorageKeys.AuthToken)}`,
      "Content-Type": "application/json",
    };

    try {
      return await this.client(url, {
        schema: responseSchema,
        method,
        body,
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: { ...defaultHeaders, ...(headers ?? {}) },
        redirect: "follow",
        referrerPolicy: "no-referrer",
      });
    } catch (err) {
      if (isResponseError(err)) {
        const { data } = err;
        const error = new Error(data.message);
        (error as any).status = err.status;
        (error as any).code = data.code;
        throw error;
      }
    }
  }
}
